import * as React from 'react';
import * as ProgressPrimitives from '@radix-ui/react-progress';
import { cn } from '~/util';

type ProgressBarProps = {
    value: number;
    max: number;
    isLabelVisible?: boolean;
    animateOnRender?: boolean;
    height?: string | number;
    width?: string | number;
    className?: string;
    indicatorClassName?: string;
    ariaLabel?: string;
    labelClassName?: string;
} & React.ComponentPropsWithoutRef<typeof ProgressPrimitives.Root>;

export const ProgressBar = React.forwardRef<
    React.ElementRef<typeof ProgressPrimitives.Root>,
    ProgressBarProps
>(
    (
        {
            value,
            max,
            isLabelVisible = false,
            animateOnRender = false,
            height = '10px',
            width = '100%',
            className,
            indicatorClassName,
            labelClassName,
            ariaLabel,
            getValueLabel,
            ...props
        },
        ref,
    ) => {
        const total = Math.max(0, Number(max) || 0);
        const done = Math.min(Math.max(0, Number(value) || 0), total);
        const pct = total > 0 ? (done / total) * 100 : 0;

        const [mounted] = React.useState(true);

        const indicatorWidth = animateOnRender
            ? mounted
                ? `${pct}%`
                : `0%`
            : `${pct}%`;

        const rootStyle: React.CSSProperties = {
            width,
            height,
        };

        const indicatorStyle: React.CSSProperties = {
            width: indicatorWidth,
            height: '100%',
            transition: animateOnRender
                ? 'width 500ms ease, background-color 150ms ease'
                : undefined,
        };

        const radixValue = max > 0 ? done : undefined;

        return (
            <ProgressPrimitives.Root
                ref={ref}
                value={radixValue}
                max={max > 0 ? max : undefined}
                aria-label={ariaLabel}
                className={cn(
                    'relative overflow-hidden rounded',
                    'shrink-0',
                    'bg-secondary-light',
                    className,
                )}
                style={rootStyle}
                {...props}
            >
                <ProgressPrimitives.Indicator
                    className={cn('block bg-primary', indicatorClassName)}
                    style={indicatorStyle}
                >
                    {isLabelVisible ? (
                        <span
                            className={cn(
                                'absolute inset-0 flex items-center justify-center text-xs font-medium text-primary-foreground',
                                labelClassName,
                            )}
                        >
                            {getValueLabel
                                ? getValueLabel(done, total)
                                : `${Math.round(pct)}%`}
                        </span>
                    ) : null}
                </ProgressPrimitives.Indicator>
            </ProgressPrimitives.Root>
        );
    },
);

ProgressBar.displayName = 'ProgressBar';
